import React, {useEffect, useState} from "react";
import {SpaceTopAndBottom} from "../../../z40-uicomp/layout";
import {useEvent, useLazyQuery} from "@soap/modules";
import {InviteUserToOrg} from "../dialogs/invite-user-modal.tsx";
import {useUserRole} from "../../../hooks/useUserRole";
import {RevokeOrgInviteDialog} from "../dialogs/RevokeOrgInviteDialog";
import {ResendOrgInviteDialog} from "../dialogs/ResendOrgInviteDialog";
import {Card, CardContent, CardHeader, CardTitle} from "../../ui/card";
import {Table, TableBody, TableCell, TableHead, TableHeader, TableRow} from "../../ui/table";
import {Button} from "../../ui/button";
import {DropdownMenu, DropdownMenuContent, DropdownMenuTrigger} from "../../ui/dropdown-menu";
import {MoreHorizontal} from "lucide-react";
import {useFormatDisplayDateTime} from "../../../utils/useFormatDisplayDate";

export function Invitations() {
    const { requestingUserIsOrgAdmin } = useUserRole();
    const formatDate = useFormatDisplayDateTime();
    const [getInvites, gotInvites] = useLazyQuery("C248v1_GetNewUserInvitations");
    const viewStates = {
        loading: 1,
        loaded: 2
    };

    const [viewState, setViewState] = useState(viewStates.loading);

    useEffect(() => {
        if (requestingUserIsOrgAdmin) {
            getInvites();
        }
    }, [requestingUserIsOrgAdmin, getInvites]);

    useEffect(() => {
        if (gotInvites !== undefined) {
            setViewState(viewStates.loaded);
        }
    }, [gotInvites]);

    useEvent({
        eventName: "E200v1_InvitationRevoked",
        onEventReceived: () => getInvites()
    })

    useEvent({
        eventName: "E201v1_OrgUserInvited",
        onEventReceived: () => getInvites()
    })

    if (!requestingUserIsOrgAdmin) {
        return null;
    }

    const invitations = gotInvites?.e199_Invites || [];

    const formatInviteDate = (dateString: string | null | undefined) => {
        if (!dateString) return "—";
        const formatted = formatDate(dateString);
        return formatted || dateString;
    };

    switch (viewState) {
        case viewStates.loading:
            return (
                <div className="flex items-center justify-center py-12">
                    <div className="flex flex-col items-center gap-3">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-action-blue"></div>
                        <span className="text-gray-600">Loading invitations...</span>
                    </div>
                </div>
            );
        case viewStates.loaded:
            return (
                <SpaceTopAndBottom>
                    <Card>
                        <CardHeader>
                            <div className="flex items-center justify-between">
                                <CardTitle>Invitations</CardTitle>
                                <InviteUserToOrg />
                            </div>
                        </CardHeader>
                        <CardContent className="p-0">
                            {invitations.length > 0 ? (
                                <div className="overflow-x-auto">
                                    <Table>
                                        <TableHeader>
                                            <TableRow>
                                                <TableHead>Email</TableHead>
                                                <TableHead>Sent On</TableHead>
                                                <TableHead>Sent By</TableHead>
                                                <TableHead className="w-[50px] sticky right-0 bg-white z-10">Actions</TableHead>
                                            </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                            {invitations.map((invite) => (
                                                <TableRow 
                                                    key={invite.e199_InviteId} 
                                                    className="group hover:bg-gray-100"
                                                >
                                                    <TableCell className="font-medium">
                                                        {invite.e199_Email}
                                                    </TableCell>
                                                    <TableCell>
                                                        {formatInviteDate(invite.e199_SentOn)}
                                                    </TableCell>
                                                    <TableCell>
                                                        {invite.e199_SentBy || "—"}
                                                    </TableCell>
                                                    <TableCell className="sticky right-0 bg-white z-10">
                                                        <DropdownMenu>
                                                            <DropdownMenuTrigger asChild>
                                                                <Button variant="ghost" size="sm">
                                                                    <MoreHorizontal className="w-4 h-4" />
                                                                </Button>
                                                            </DropdownMenuTrigger>
                                                            <DropdownMenuContent align="end">
                                                                <ResendOrgInviteDialog 
                                                                    inviteId={invite.e199_InviteId} 
                                                                    email={invite.e199_Email}
                                                                />
                                                                <RevokeOrgInviteDialog 
                                                                    inviteId={invite.e199_InviteId} 
                                                                    email={invite.e199_Email}
                                                                />
                                                            </DropdownMenuContent>
                                                        </DropdownMenu>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </div>
                            ) : (
                                <div className="p-8 text-center text-gray-500">
                                    No invitations found
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </SpaceTopAndBottom>
            );
    }
}

